/**
 * Repository access (ADR 029): which credential the platform uses for each repository, the tenant's own Git
 * tokens, and a live "test a URL". Route: /configuration/repository-access.
 */
import { AlertCircle } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  GitCredentialsTable,
  RemoveCredentialDialog,
  RoutingRuleDialog,
  RoutingRulesTable,
  SetGitCredentialDialog,
  TestUrlPanel,
} from '@/components/repository-access'
import { useRepositoryAccess } from '@/hooks/useRepositoryAccess'
import { fallbackAfterDelete } from '@/lib/gitRouting'

export function RepositoryAccessPage() {
  const page = useRepositoryAccess()
  const deletingFallback = page.deletingRule
    ? fallbackAfterDelete(page.deletingRule, page.rules, page.deployment)
    : undefined

  return (
    <PageShell>
      <div className="space-y-6 p-6">
        <PageHeader
          title="Repository access"
          description="Which credential the platform uses for each repository. First matching rule wins."
        />

        {page.rulesError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not load the routing rules</AlertTitle>
            <AlertDescription>{page.rulesError.message}</AlertDescription>
          </Alert>
        )}

        <RoutingRulesTable
          rules={page.rules}
          isLoading={page.rulesLoading}
          deployment={page.deployment}
          credentials={page.knownCredentials}
          canEdit={page.canEdit}
          canManageTokens={page.canManageTokens && page.credentialsAvailable}
          isReordering={page.isReordering}
          onAdd={() => page.openAddRule()}
          onEdit={page.openEditRule}
          onDelete={page.setDeletingRule}
          onReorder={page.reorderRules}
          onTest={page.testWithRule}
          onSetToken={(name) => page.openSetToken(name)}
        />

        <GitCredentialsTable
          credentials={page.credentials}
          isLoading={page.credentialsLoading}
          available={page.credentialsAvailable}
          unavailable={page.credentialsUnavailable}
          rules={page.rules}
          deployment={page.deployment}
          canManageTokens={page.canManageTokens}
          onSetToken={(name) => page.openSetToken(name)}
          onRemove={page.setRemovingCredential}
        />

        <TestUrlPanel
          url={page.testUrl}
          onUrlChange={page.setTestUrl}
          onTest={page.runTest}
          inputRef={page.testInputRef}
          isTesting={page.isTesting}
          result={page.testResult}
          error={page.testError}
          credentials={page.knownCredentials}
          canEdit={page.canEdit}
          canManageTokens={page.canManageTokens && page.credentialsAvailable}
          onAddRule={page.openAddRule}
          onSetToken={page.openSetToken}
        />
      </div>

      <RoutingRuleDialog
        open={page.ruleDialog.open}
        onOpenChange={page.setRuleDialogOpen}
        rule={page.ruleDialog.rule}
        draft={page.ruleDialog.draft}
        rules={page.rules}
        credentialNames={page.credentialNames}
        onSubmit={page.saveRule}
      />

      <SetGitCredentialDialog
        open={page.tokenDialog.open}
        onOpenChange={page.setTokenDialogOpen}
        name={page.tokenDialog.name}
        replacing={page.tokenDialog.replacing}
        usedBy={page.tokenUsedBy}
        onSubmit={page.saveToken}
      />

      <ConfirmDialog
        open={page.deletingRule !== undefined}
        onOpenChange={(open) => !open && page.setDeletingRule(undefined)}
        title={`Delete rule ${page.deletingRule?.position ?? ''}?`}
        description={
          `${page.deletingRule?.urlPattern ?? ''} will fall back to ${deletingFallback ?? 'nothing — no other rule or default covers it'}.`
          + ' Reviews of these repositories may start failing with 404.'
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={page.isDeletingRule}
        onConfirm={page.confirmDeleteRule}
      />

      <RemoveCredentialDialog
        credential={page.removingCredential}
        usedBy={page.removingCredentialUsedBy}
        isRemoving={page.isRemovingCredential}
        onClose={() => page.setRemovingCredential(undefined)}
        onConfirm={page.confirmRemoveCredential}
      />
    </PageShell>
  )
}

export default RepositoryAccessPage
